/**
 * Deterministic (no LLM) location checks run at Finalize on the Lines step,
 * after hard validation (lineItemValidation.js) passes (Issue 15 retest,
 * decided 2026-07-21). Both checks are non-blocking - they only drive a
 * confirm dialog, never forbid finalizing.
 */
import { effectiveItemType } from './lineItemValidation';

const setsEqual = (a, b) => a.size === b.size && [...a].every((v) => b.has(v));

// One entry per distinct non-blank ship_to_location, carrying the lines
// assigned to it. Lines with no location set are excluded from every
// group (nothing to confirm about a location that isn't set).
export const groupLinesByLocation = (lineItems) => {
  const byLocation = new Map();
  (lineItems || []).forEach((item) => {
    const loc = (item?.ship_to_location || '').trim();
    if (!loc) return;
    if (!byLocation.has(loc)) byLocation.set(loc, { location: loc, lines: [] });
    byLocation.get(loc).lines.push(item);
  });
  return Array.from(byLocation.values()).map((entry) => ({
    ...entry,
    count: entry.lines.length,
  }));
};

export const isMultiLocation = (lineItems) => groupLinesByLocation(lineItems).length > 1;

// Compares the location set used by goods lines against the location set
// used by services lines. Only meaningful when both sets are non-empty -
// a pure-goods or pure-services line set can never diverge by definition.
export const getGoodsServiceLocationDivergence = (lineItems, procurementType) => {
  const goodsLocations = new Set();
  const serviceLocations = new Set();
  (lineItems || []).forEach((item) => {
    const loc = (item?.ship_to_location || '').trim();
    if (!loc) return;
    const itemType = effectiveItemType(item, procurementType);
    (itemType === 'services' ? serviceLocations : goodsLocations).add(loc);
  });
  const diverges =
    goodsLocations.size > 0 && serviceLocations.size > 0 && !setsEqual(goodsLocations, serviceLocations);
  return {
    diverges,
    goodsLocations: Array.from(goodsLocations),
    serviceLocations: Array.from(serviceLocations),
  };
};

// Single entry point StepLineItems calls at Finalize click time, after hard
// validation passes, to decide whether to show the multi-location /
// goods-service-divergence confirm dialog (one dialog covers both checks).
export const summarizeLocationConfirmation = (lineItems, procurementType) => {
  const groups = groupLinesByLocation(lineItems);
  const divergence = getGoodsServiceLocationDivergence(lineItems, procurementType);
  return {
    needsConfirmation: groups.length > 1,
    groups,
    divergence,
  };
};
